import React, { useState } from "react";

import {
  ChatBubbleLeftRightIcon,
  Cog8ToothIcon,
} from "@heroicons/react/24/outline";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";

import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";

import UserProfilePopover from "./UserProfilePopover";

import { classNames } from "../utils/styles";
import { taskStatsAtom } from "../store/taskStatsAtom";
import { useRecoilValue } from "recoil";
import PlanUsageCard from "../components/PlanUsageCard";
import GlobalLoadingSpinner from "../components/GlobalSpinner";
import { billingAtom } from "../store/billingAtom";

const SideBar = ({ navigation, user, currentPath, onClose }) => {
  const billingInfo = useRecoilValue(billingAtom);

  const taskStats = useRecoilValue(taskStatsAtom);

  const [openItems, setOpenItems] = useState({ Setup: true });

  const toggleItem = name => {
    setOpenItems(prev => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      localStorage.removeItem("supabase_jwt");
      localStorage.removeItem("supabase.auth.token");
      sessionStorage.removeItem("company_id");
      sessionStorage.removeItem("user_email");
      localStorage.removeItem("chatbot_uuid");
      localStorage.clear();

      navigate("/onboarding/signup");
    } catch (error) {
      console.error("Error logging out:", error.message);
    }
  };

  return (
    <div className="flex grow flex-col overflow-y-auto bg-white px-6 pb-4 border border-gray-300">
      <div className="flex h-16 shrink-0 items-center">
        <img src="/logo.png" alt="SupportMagic" className="h-8 w-auto" />
      </div>
      <nav className="flex flex-1 flex-col">
        <ul className="flex flex-1 flex-col gap-y-7">
          <li>
            <ul className="-mx-2 space-y-1">
              {navigation.map(item => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    onClick={() => {
                      toggleItem(item.name);
                      if (onClose) {
                        onClose();
                      }
                    }}
                    className={classNames(
                      item.aggregatorMenu
                        ? ""
                        : item.current
                          ? "bg-gray-50 text-slate-700"
                          : "text-slate-700 hover:bg-gray-50 hover:text-slate-800",
                      "group flex items-center gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 cursor-pointer"
                    )}
                  >
                    <item.icon
                      aria-hidden="true"
                      className="size-6 shrink-0 text-gray-500"
                    />
                    {item.name}
                    {item.children && (
                      <span className="ml-auto">
                        {openItems[item.name] ? (
                          <ChevronUpIcon
                            aria-hidden="true"
                            className="ml-2 h-5 w-5 text-slate-700"
                          />
                        ) : (
                          <ChevronDownIcon
                            aria-hidden="true"
                            className="ml-2 h-5 w-5 text-slate-700"
                          />
                        )}
                      </span>
                    )}

                    {item.counter && (
                      <span className="ml-auto bg-gray-50 border border-gray-300 text-slate-700 text-xs font-medium px-2 py-0.5 rounded-full">
                        {taskStats.tasks.open}
                      </span>
                    )}
                  </Link>
                  {item.children && openItems[item.name] && (
                    <ul className="mt-1 space-y-1">
                      {item.children.map(subItem => (
                        <li key={subItem.name}>
                          <Link
                            onClick={onClose}
                            to={subItem.href}
                            className={`${currentPath === subItem.href ? "bg-gray-50 text-slate-700" : ""} text-slate-700 hover:bg-gray-50 hover:text-slate-800 flex items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold pl-8`}
                          >
                            {subItem.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </li>
        </ul>

        <ul className="mb-2">
          <li className="mb-2">
            <Link
              onClick={onClose}
              to="/settings/company_info"
              className={`${currentPath === "/settings/company_info" ? "bg-gray-50 text-slate-700" : ""} group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-slate-700 hover:bg-gray-50 hover:text-slate-800`}
            >
              <Cog8ToothIcon aria-hidden="true" className="h-6 w-6 shrink-0" />
              Settings
            </Link>
          </li>

          <li className="mb-4">
            <Link
              target="_blank"
              to="mailto:help@supportmagic.ai"
              className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-slate-700 hover:bg-gray-50 hover:text-slate-800 w-full relative"
            >
              <ChatBubbleLeftRightIcon
                aria-hidden="true"
                className="h-6 w-6 shrink-0"
              />
              Support
              <span className="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200 absolute right-2">
                <svg
                  viewBox="0 0 6 6"
                  aria-hidden="true"
                  className="size-1.5 fill-green-500"
                >
                  <circle r={3} cx={3} cy={3} />
                </svg>
                Online
              </span>
            </Link>
          </li>

          <li className="mb-4 relative rounded-xl overflow-hidden">
            {!billingInfo && <GlobalLoadingSpinner />}
            <PlanUsageCard />
          </li>

          <li className="mt-auto relative rounded-xl">
            {!user?.email && <GlobalLoadingSpinner />}
            <UserProfilePopover
              user={user}
              onSignOut={handleSignOut}
              onClose={onClose}
            />
          </li>
        </ul>
      </nav>
    </div>
  );
};

export default SideBar;
